<!--
  PayrollConciliationStatusCard.vue
  
  Purpose: Reusable status card component for conciliation steps in payroll wizard
  
  Responsibilities:
  - Display conciliation status (pending, in_progress, completed) with visual indicators
  - Show summary grid with key metrics (status, count, amount)
  - Display action button for managing conciliation (opens wizard/modal)
  - Show status-specific info messages with appropriate styling
  - Provide consistent UI pattern across all conciliation steps
  
  Used by:
  - Step3RevenueConciliationSummary (revenue conciliation)
  - Step4ExpenseConciliationSummary (expense conciliation)
  - StepCashRecebimentos (advances conciliation)
  - StepPayrollRefunds (refunds status)

  Props:
  - status: string - Current status ('pending' | 'in_progress' | 'completed')
  - summaryItems: Array - Items to display in summary grid
  - showAction: boolean - Whether to show action button
  - buttonLabel: string - Label for action button
  - buttonIcon: string - Icon for action button
  - buttonSeverity: string - Button severity (primary, secondary, etc.)
  - statusMessage: string|null - Custom status message (overrides default)
  
  Events:
  - action: Emitted when action button is clicked
  
  Benefits:
  - Eliminates 200+ lines of duplicated code across step components
  - Consistent UI/UX across all conciliation steps
  - Easy to update styling/behavior in one place
  - Makes step components smaller and more focused
  
  Data Flow:
  - Receives status, summaryItems, buttonLabel, etc. as props
  - Emits 'action' event when action button is clicked
  - Parent component handles action (opens conciliation wizard/modal)
-->
<template>
  <div class="conciliation-card" data-testid="payroll-shared-conciliation-status-card">
    <div class="card-body">
      <!-- Summary Grid -->
      <PayrollSummaryGrid :items="summaryItems" />

      <!-- Action Button (if showAction is true) -->
      <div v-if="showAction" class="action-section">
        <AppButton
          :label="buttonLabel"
          :icon="buttonIcon"
          :severity="buttonSeverity"
          size="large"
          class="action-button"
          data-testid="payroll-shared-conciliation-action-btn"
          @click="handleAction"
        />
      </div>

      <!-- Info Messages based on status -->
      <div v-if="status === 'pending'" class="info-message-inline pending">
        <i class="pi pi-info-circle"></i>
        <span>{{ statusMessage || t('Clique no botão acima para iniciar a conciliação.') }}</span>
      </div>

      <div v-else-if="status === 'in_progress'" class="info-message-inline in-progress">
        <i class="pi pi-sync"></i>
        <span>{{ statusMessage || t('Conciliação em progresso. Você pode continuar adicionando itens ou finalizar o processo.') }}</span>
      </div>

      <div v-else-if="status === 'completed'" class="info-message-inline completed">
        <i class="pi pi-check-circle"></i>
        <span>{{ statusMessage || t('Conciliação concluída. Você pode editar clicando no botão acima.') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import PayrollSummaryGrid from './PayrollSummaryGrid.vue';
import type { SummaryItem } from './PayrollSummaryGrid.vue';

type ConciliationStatus = 'pending' | 'in_progress' | 'completed';

interface Props {
  status: ConciliationStatus;
  summaryItems: SummaryItem[];
  showAction?: boolean;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonSeverity?: string;
  statusMessage?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  showAction: true,
  buttonLabel: '',
  buttonIcon: 'pi pi-cog',
  buttonSeverity: 'primary',
  statusMessage: null,
});

const emit = defineEmits<{
  'action': [];
}>();

const { t } = useI18n();

const handleAction = (): void => {
  emit('action');
};
</script>

<style scoped>
.conciliation-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  margin-bottom: 1.5rem;
}

.card-body {
  padding: 1.5rem;
}

.action-section {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.action-button {
  min-width: 200px;
}

.info-message-inline {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 6px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.info-message-inline.pending {
  background: var(--blue-50);
  color: var(--blue-800);
  border-left: 3px solid var(--blue-500);
}

.info-message-inline.pending i {
  color: var(--blue-500);
}

.info-message-inline.in-progress {
  background: var(--orange-50);
  color: var(--orange-800);
  border-left: 3px solid var(--orange-500);
}

.info-message-inline.in-progress i {
  color: var(--orange-500);
}

.info-message-inline.completed {
  background: var(--green-50);
  color: var(--green-800);
  border-left: 3px solid var(--green-500);
}

.info-message-inline.completed i {
  color: var(--green-500);
}
</style>
